import React from "react";

import darkBackground from "../../images/custom-systems-dark-background.svg";
import marnusImage from "../../images/Marnus02.png";
import discoverIcon from "../../images/icon-discover.svg";
import designIcon from "../../images/icon-design.svg";
import buildIcon from "../../images/icon-build.svg";
import integrateIcon from "../../images/icon-integrate.svg";

import "./CustomSystemsSection.css";

const processSteps = [
  {
    number: "01",
    title: "Discover",
    description:
      "Understand goals, processes and the data landscape.",
    compactDescription:
      "Understand goals, processes and data.",
    icon: discoverIcon,
    tone: "blue",
  },
  {
    number: "02",
    title: "Design",
    description:
      "Architect the right solution and intelligent workflows.",
    compactDescription:
      "Architect the solution and workflows.",
    icon: designIcon,
    tone: "pink",
  },
  {
    number: "03",
    title: "Build",
    description:
      "Engineer, test and refine with measurable outcomes.",
    compactDescription:
      "Engineer, test and refine outcomes.",
    icon: buildIcon,
    tone: "blue",
  },
  {
    number: "04",
    title: "Integrate",
    description:
      "Deploy, connect and optimise for real-world impact.",
    compactDescription:
      "Deploy, connect and optimise.",
    icon: integrateIcon,
    tone: "pink",
  },
];

function ArrowRight({ className = "" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M5 12h14" />
      <path d="m13 6 6 6-6 6" />
    </svg>
  );
}

function ProcessStep({ step }) {
  return (
    <article
      className={`custom-process__step custom-process__step--${step.tone}`}
      role="listitem"
    >
      <img
        className="custom-process__icon"
        src={step.icon}
        alt=""
        width="72"
        height="72"
        aria-hidden="true"
      />

      <div className="custom-process__content">
        <div className="custom-process__heading">
          <span
            className={`custom-process__number custom-process__number--${step.tone}`}
          >
            {step.number}
          </span>

          <h3>{step.title}</h3>
        </div>

        <p>
          <span className="custom-process__description--full">
            {step.description}
          </span>

          <span className="custom-process__description--compact">
            {step.compactDescription}
          </span>
        </p>
      </div>
    </article>
  );
}

export default function CustomSystemsSection() {
  return (
    <section
      id="custom-systems"
      className="custom-systems"
      aria-labelledby="custom-systems-title"
    >
      <img
        className="custom-systems__background"
        src={darkBackground}
        alt=""
        aria-hidden="true"
      />

      <div className="custom-systems__inner">
        <div className="custom-systems__main">
          <div className="custom-systems__copy">
            <p className="custom-systems__eyebrow">
              Custom Intelligent Systems
            </p>

            <h2 id="custom-systems-title">
              <span>Build intelligence</span>
              <span>into the way your</span>
              <span>business works.</span>
            </h2>

            <p className="custom-systems__body">
              We scope, design and build AI-enabled platforms, CRMs and
              operational workflows that remove friction, automate routine
              decisions and turn complex processes into measurable
              productivity.
            </p>

            <div className="custom-systems__actions">
              <a
                className="custom-systems__button custom-systems__button--primary"
                href="#contact"
              >
                <span>Scope your system</span>
                <ArrowRight />
              </a>

              <a
                className="custom-systems__button custom-systems__button--secondary"
                href="#custom-process"
              >
                <span>See how we work</span>
                <ArrowRight />
              </a>
            </div>
          </div>

          <div className="custom-systems__visual">
            <img
              className="custom-systems__person"
              src={marnusImage}
              alt="Marnus Janse van Rensburg, Lead Consultant in Applied Intelligent Systems"
              width="1048"
              height="1340"
              loading="lazy"
            />

            <aside
              className="custom-systems__profile"
              aria-label="Consultant profile"
            >
              <span className="custom-systems__profile-eyebrow">
                AI Consulting with
              </span>

              <strong>Marnus Janse van Rensburg</strong>

              <span>
                Lead Consultant · Applied Intelligent Systems
              </span>

              <span>
                PhD Candidate in Machine Learning
              </span>
            </aside>
          </div>
        </div>

        <div
          id="custom-process"
          className="custom-process"
          aria-label="Our custom systems development process"
        >
          <p className="custom-process__label">
            From operational need to working system
          </p>

          <div className="custom-process__grid" role="list">
            {processSteps.map((step) => (
              <ProcessStep
                key={step.number}
                step={step}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}